import { MapPin, Coffee, Clock, CalendarCheck, CalendarX, Flag } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ItinerarySummary } from '@/types/itinerary';
import type { PreviewRow } from '@/lib/itineraryPreview';
import { unscheduledReasonText } from '@/lib/itineraryPreview';

/**
 * ItineraryTimeline — the generated plan as a connected vertical timeline.
 * Ported from the reference `ItineraryTimeline`, re-skinned from MUI/`sx` to
 * shadcn/Tailwind tokens. Three row kinds:
 *   - ENTRY:       crimson node, park-entry marker.
 *   - ATTRACTION:  gold-ringed dot + card with the "Jadwal dipilih" slot window,
 *                  or a muted "Tidak ada jadwal tersedia" state + reason.
 *   - BREAK:       dashed divider card for the lunch / rest interval.
 * Presentational only — all data (incl. the schedule state) is precomputed by
 * `buildPreviewRows`.
 */

/** Display the dot-separated clock style (10.00 not 10:00). */
function formatClock(t: string | null | undefined): string {
  return typeof t === 'string' ? t.replace(/:/g, '.') : '';
}

export function ItineraryTimeline({ items }: { items: PreviewRow[] }) {
  return (
    <div className="relative w-full">
      {/* Continuous vertical rail behind the nodes. */}
      <div
        aria-hidden
        className="absolute bottom-3.5 left-[26px] top-3.5 w-0.5 rounded bg-gradient-to-b from-primary/30 via-gold-500/60 to-primary/30"
      />
      {items.map((item, idx) => (
        <TimelineRow key={`${idx}-${item.type}-${item.attractionId ?? item.label ?? idx}`} item={item} />
      ))}
    </div>
  );
}

/** Shared node-column wrapper so every row's dot lands on the same rail. */
function Row({ children }: { children: React.ReactNode }) {
  return <div className="relative min-h-10 pb-4 pl-16">{children}</div>;
}

function TimelineRow({ item }: { item: PreviewRow }) {
  if (item.type === 'ENTRY') return <EntryRow item={item} />;
  if (item.type === 'BREAK') return <BreakRow item={item} />;
  return <AttractionRow item={item} />;
}

/** ENTRY — crimson node + park-entry headline. */
function EntryRow({ item }: { item: PreviewRow }) {
  return (
    <Row>
      <div className="absolute left-0 top-0.5 flex w-[52px] justify-center">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm ring-2 ring-gold-500/40">
          <MapPin className="h-5 w-5" />
        </div>
      </div>
      <div className="flex flex-wrap items-baseline gap-2 pt-1">
        <span className="tnum text-lg font-extrabold tracking-wide text-primary">
          {formatClock(item.time)}
        </span>
        <span className="text-base font-bold text-ink-900">{item.label || 'Masuk Autopia'}</span>
        <span className="rounded-full border border-gold-500/50 bg-gold-200/40 px-2 py-0.5 text-[0.66rem] font-bold uppercase tracking-wide text-primary">
          Pintu Masuk
        </span>
      </div>
    </Row>
  );
}

/** ATTRACTION — gold-ringed dot + card. Scheduled shows the slot window;
 *  unscheduled shows a muted "no schedule" pill + the reason. */
function AttractionRow({ item }: { item: PreviewRow }) {
  const unscheduled = item.assignedSchedule?.state === 'none';
  const hasTime = Boolean(item.startTime && item.endTime);
  const reason = unscheduled ? unscheduledReasonText(item.unscheduledReason) : null;

  return (
    <Row>
      <div className="absolute left-0 top-2 flex w-[52px] justify-center">
        <div
          className={cn(
            'h-4 w-4 rounded-full',
            unscheduled
              ? 'border-2 border-dashed border-primary/40 bg-cream-50'
              : 'border-[3px] border-primary bg-card ring-2 ring-gold-500/40',
          )}
        />
      </div>

      <div
        className={cn(
          'rounded-xl border-2 px-3 py-2.5 shadow-sm transition-transform',
          unscheduled
            ? 'border-dashed border-primary/25 border-l-primary/40 bg-cream-50/60'
            : 'border-gold-500/30 border-l-[3px] border-l-primary bg-card',
        )}
      >
        <div className="mb-1 flex items-center gap-2">
          {/* Execution window — only when scheduled. */}
          {hasTime && (
            <span className="tnum inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-sm font-bold tracking-wide text-primary">
              {formatClock(item.startTime)}
              <span className="opacity-50">–</span>
              {formatClock(item.endTime)}
            </span>
          )}
          {item.code && (
            <span className="rounded border border-dashed border-gold-500/60 px-1.5 py-0.5 text-[0.62rem] font-extrabold tracking-widest text-gold-600">
              {item.code}
            </span>
          )}
        </div>

        <p className="text-[0.97rem] font-bold leading-tight text-ink-900">{item.name}</p>

        {item.durationMinutes != null && (
          <div className="mt-1 flex items-center gap-1 text-xs font-semibold text-gold-600">
            <Clock className="h-3.5 w-3.5" />
            Durasi {item.durationMinutes} mnt
          </div>
        )}

        {/* The bookable slot apply will lock in — gold "Jadwal dipilih" pill. */}
        {item.assignedSchedule?.state === 'ok' && (
          <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-gold-500/50 bg-gradient-to-br from-gold-200 to-gold-500/70 px-2.5 py-1 shadow-sm">
            <CalendarCheck className="h-3.5 w-3.5 text-primary" />
            <span className="text-[0.68rem] font-bold text-primary">Jadwal dipilih</span>
            <span className="tnum text-xs font-extrabold text-ink-900">
              {formatClock(item.assignedSchedule.time)}
            </span>
          </div>
        )}
        {unscheduled && (
          <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-primary/25 bg-primary/5 px-2.5 py-1">
            <CalendarX className="h-3.5 w-3.5 text-ink-400" />
            <span className="text-[0.7rem] font-bold text-ink-600">Tidak ada jadwal tersedia</span>
          </div>
        )}

        {/* Why it couldn't be placed — each reason implies a different move. */}
        {reason && (
          <div className="mt-1.5">
            <span className="inline-flex items-center rounded-md border border-dashed border-border bg-black/[0.02] px-2 py-0.5 text-xs font-bold text-ink-600">
              {reason.label}
            </span>
            <p className="mt-1 text-xs leading-snug text-ink-400">{reason.hint}</p>
          </div>
        )}
      </div>
    </Row>
  );
}

/** BREAK — dashed divider card for the lunch / rest interval. */
function BreakRow({ item }: { item: PreviewRow }) {
  return (
    <Row>
      <div className="absolute left-0 top-1.5 flex w-[52px] justify-center">
        <div className="flex h-[30px] w-[30px] items-center justify-center rounded-full border-2 border-dashed border-gold-500/60 bg-cream-50 text-gold-600">
          <Coffee className="h-4 w-4" />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-dashed border-gold-500/60 bg-cream-50/50 px-3.5 py-2">
        <span className="text-sm font-bold text-gold-600">
          {item.label || 'Jeda Istirahat / Makan Siang'}
        </span>
        {item.startTime && item.endTime && (
          <span className="tnum ml-auto text-sm font-bold text-gold-600">
            {formatClock(item.startTime)} – {formatClock(item.endTime)}
          </span>
        )}
      </div>
    </Row>
  );
}

/** A small "Selesai" flag below the last row (decorative). */
export function TimelineEndCap({ summary }: { summary: ItinerarySummary | null }) {
  if (!summary?.lastEndTime) return null;
  return (
    <div className="relative mt-1 pl-16">
      <div className="absolute left-0 top-0 flex w-[52px] justify-center">
        <div className="flex h-[30px] w-[30px] items-center justify-center rounded-full bg-gradient-to-br from-gold-600 to-gold-500 text-primary shadow-sm">
          <Flag className="h-4 w-4" />
        </div>
      </div>
      <p className="pt-1 text-sm font-bold text-primary">Selesai · {formatClock(summary.lastEndTime)}</p>
    </div>
  );
}
